import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { MarketingCampaignEmail, useMarketingCampaignsEmail } from '../../queries/marketingCampaignsEmail';
import { MarketingCampaignFormDialog } from './MarketingCampaignFormDialog';

interface MarketingCampaignsPageProps {
  initialAiContext?: string;
  initialListIds?: number[];
  onConsumeInitialAiContext?: () => void;
}

export function MarketingCampaignsPage({ initialAiContext, initialListIds, onConsumeInitialAiContext }: MarketingCampaignsPageProps) {
  const { data: campaigns = [], isLoading } = useMarketingCampaignsEmail();
  const [editing, setEditing] = useState<MarketingCampaignEmail | null | 'new'>(initialAiContext ? 'new' : null);

  const openCreate = () => {
    setEditing('new');
  };

  const closeDialog = () => {
    setEditing(null);
    onConsumeInitialAiContext?.();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Manually composed or AI-drafted promotional emails.</p>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          Create campaign
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td className="px-3 py-2 font-medium">{campaign.campaign_name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{campaign.subject_line}</td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{campaign.status}</span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{campaign.sent_at ? new Date(campaign.sent_at).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(campaign)} className="text-sm font-medium text-primary hover:underline">
                      {campaign.status === 'sent' ? 'View' : 'Edit & Send'}
                    </button>
                  </td>
                </tr>
              ))}
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">
                    No marketing campaigns yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <MarketingCampaignFormDialog
          initialListIds={initialListIds}
          campaign={editing === 'new' ? null : editing}
          initialAiContext={editing === 'new' ? initialAiContext : undefined}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
